import { SdkContext } from '../internal'
import { fetchFile, type DownloadedFile } from '../fileDownload'
import type { MyReferral, ReferralReport, ReferralSettings } from '@/types/referrals'

/** Only the dates that were chosen. */
const rangeQuery = (range: { from?: string; to?: string }) =>
  Object.fromEntries(Object.entries(range).filter(([, v]) => v))

/** F-159: referrals. Paths written by hand (F-03). */
export class ReferralsResource {
  constructor(private ctx: SdkContext) {}

  /** Owner and admin. */
  async getSettings(): Promise<ReferralSettings> {
    return this.ctx.call({ httpMethod: 'GET', path: '/referrals/settings' })
  }

  /** Owner and admin. */
  async updateSettings(enabled: boolean): Promise<ReferralSettings> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/referrals/settings', hasBody: true }, { body: { enabled } })
  }

  /** Any member: the own link and how many joined through it. Off: `enabled: false`. */
  async me(): Promise<MyReferral> {
    return this.ctx.call({ httpMethod: 'GET', path: '/referrals/me' })
  }

  /** Owner, admin and moderator. `pagination` sits beside the object `data`, so the envelope is read whole. */
  async report(params: { page?: number; limit?: number; from?: string; to?: string } = {}): Promise<ReferralReport> {
    const body = await this.ctx.envelope<{ data?: Partial<ReferralReport>; pagination?: ReferralReport['pagination'] }>(
      { httpMethod: 'GET', path: '/referrals/report' },
      { query: { ...rangeQuery(params), page: params.page ?? 1, limit: params.limit ?? 20 } }
    )
    return {
      items: body?.data?.items || [],
      totals: body?.data?.totals || { referrers: 0, referrals: 0 },
      pagination: body?.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 }
    }
  }

  /** Every row as CSV. */
  async reportCsv(range: { from?: string; to?: string } = {}): Promise<DownloadedFile> {
    return fetchFile(this.ctx, '/referrals/report', { ...rangeQuery(range), format: 'csv' }, 'text/csv', 'referrals.csv')
  }
}
